import { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  getScannedDocument,
  updateScannedDocument,
} from '../../../../../../services/api/scannedDocuments';
import { useIntl } from 'react-intl';
import ScannedDocumentsLayout from '../../Commons/ScannedDocumentsLayout';
import { MainContext } from '../../../../../../context/MainContext';
import LayoutHeader from '../Commons/LayoutHeader';
import SuccessPage from '../../Commons/ScannedDocumentsLayout/components/SuccessPage';
import { updateProjectTemplate } from '../../../../../../services/api/projectTemplates';
import laboratoryFieldGroups from '../Commons/laboratoryFieldGroups';
import { checkDiffTemplate } from '../../Commons/ScannedDocumentsLayout/utils.index';

const transform = (loadedLaboratoryNote) => {
  const newArray = [];
  [3, 7, 28, 60].forEach((element) => {
    if (
      loadedLaboratoryNote[`compressive_strength_${element}_1`] !== null ||
      loadedLaboratoryNote[`compressive_strength_${element}_2`] !== null ||
      loadedLaboratoryNote[`compressive_strength_${element}_3`] !== null
    ) {
      newArray.push({
        day: element || '',
        value1: loadedLaboratoryNote[`compressive_strength_${element}_1`] || '',
        value2: loadedLaboratoryNote[`compressive_strength_${element}_2`] || '',
        value3: loadedLaboratoryNote[`compressive_strength_${element}_3`] || '',
      });
    }
  });
  const csRegex = new RegExp(/^compressive_strength_(\d*)_(\d)/);
  const days = new Set();
  const extraStrength =
    loadedLaboratoryNote.strength_extra ||
    loadedLaboratoryNote.ave_strength_extra ||
    {};
  Object.keys(extraStrength).forEach((key) => {
    const dayRegexArray = csRegex.exec(key);
    if (dayRegexArray) days.add(dayRegexArray[1]);
  });
  days.forEach((day) => {
    newArray.push({
      day: day,
      value1: extraStrength[`compressive_strength_${day}_1`] || '',
      value2: extraStrength[`compressive_strength_${day}_2`] || '',
      value3: extraStrength[`compressive_strength_${day}_3`] || '',
    });
  });
  return newArray;
};

const UpdateLaboratoryScannedDoc = () => {
  const intl = useIntl();
  const { project } = useOutletContext();
  const { setLoading, setError } = useContext(MainContext);
  const { sdId, model } = useParams();
  const [initialData, setInitialData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [diffTemplate, setDiffTemplate] = useState(false);
  const [template, setLoadedTemplate] = useState(null);
  const [isScannedDocumentValidated, setIsScannedDocumentValidated] =
    useState(false);
  const noTemplateMsg = intl.formatMessage({ id: 'noTemplate' });
  const noSupplierMsg = intl.formatMessage({ id: 'noSuppliers' });
  const navigate = useNavigate();

  const updateDeliveryScannedDocument = async (
    regions,
    formState,
    fileName,
  ) => {
    try {
      setLoading(true);

      const compressiveStrength = formState.compressive_strength;
      const defaultCompressiveStrength = {};
      const extraCompressiveStrength = {};

      compressiveStrength.forEach((item) => {
        const day = Number(item.day);
        if (day === 3 || day === 7 || day === 28 || day === 60) {
          Object.entries(item).forEach(([key, value]) => {
            if (key !== 'day') {
              defaultCompressiveStrength[
                `compressive_strength_${day}_${key.split('value')[1]}`
              ] = parseFloat(value);
            }
          });
        } else {
          Object.entries(item).forEach(([key, value]) => {
            if (key !== 'day') {
              extraCompressiveStrength[
                `compressive_strength_${day}_${key.split('value')[1]}`
              ] = parseFloat(value);
            }
          });
        }
      });
      const scannedDocument = await updateScannedDocument(
        sdId,
        {
          docket_number: formState.docket_number,
          laboratory_note_data: {
            compressive_strength_3_1: null,
            compressive_strength_3_2: null,
            compressive_strength_3_3: null,
            compressive_strength_7_1: null,
            compressive_strength_7_2: null,
            compressive_strength_7_3: null,
            compressive_strength_28_1: null,
            compressive_strength_28_2: null,
            compressive_strength_28_3: null,
            compressive_strength_60_1: null,
            compressive_strength_60_2: null,
            compressive_strength_60_3: null,
            ...formState,
            ...defaultCompressiveStrength,
            strength_extra: extraCompressiveStrength,
          },
          regions: regions,
          fileName: fileName,
        },
        'laboratory',
      );

      if (scannedDocument.data) {
        setSuccess(true);
        if (checkDiffTemplate(regions, template.regions)) {
          setDiffTemplate(regions);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLoading(true);
    const loadTemplate = async () => {
      try {
        const loadedSD = (await getScannedDocument(sdId, 'laboratory')).data;
        let readedTemplate = loadedSD.template_data || {};
        setIsScannedDocumentValidated(loadedSD.validated);
        setLoadedTemplate(readedTemplate);
        let loadedLaboratoryNote =
          loadedSD.laboratory_note_data ||
          loadedSD.no_validated_laboratory_note_data;
        const loadedLab = readedTemplate?.laboratory_data || {};
        const csArray = transform(loadedLaboratoryNote);
        setInitialData({
          form: {
            templateName:
              readedTemplate.name || intl.formatMessage({ id: 'noTemplate' }),
            supplier_id:
              readedTemplate.supplier ||
              loadedLaboratoryNote.laboratory_data.name ||
              intl.formatMessage({ id: 'noSuppliers' }),
            phone_number:
              loadedLab.phone_number ||
              loadedLaboratoryNote.laboratory_data.phone_number,
            email:
              loadedLab.email || loadedLaboratoryNote.laboratory_data.email,
          },
          regions: loadedSD.regions,
          regionsValue: {
            docket_number: loadedSD.docket_number,
            reference_number: loadedLaboratoryNote.reference_number,
            compressive_strength: csArray,
          },
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error loading initial data');
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [
    setLoading,
    setInitialData,
    sdId,
    noTemplateMsg,
    noSupplierMsg,
    model,
    setError,
    intl,
  ]);

  if (success) {
    return (
      <SuccessPage
        title={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.edit.success.title',
        })}
        subtitle={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.edit.success.subtitle',
        })}
        checkDiffTemplate={Boolean(diffTemplate)}
        onBackToOcr={() =>
          navigate(isScannedDocumentValidated ? `../validated` : `../noValidated`)
        }
        onSaveTemplate={async () => {
          const newTemplate = { ...template, regions: diffTemplate };
          setLoading(true);
          await updateProjectTemplate(newTemplate);
          setLoading(false);
          navigate(isScannedDocumentValidated ? `../validated` : `../noValidated`, {
            replace: true,
          });
        }}
      />
    );
  }
  return (
    <ScannedDocumentsLayout
      model="laboratory"
      initialData={initialData}
      fieldGroups={laboratoryFieldGroups}
      validableFields={laboratoryFieldGroups.mandatory}
      onFinish={updateDeliveryScannedDocument}
      onBack={() => navigate(isScannedDocumentValidated ? `../validated` : `../noValidated`)}
      mode={!isScannedDocumentValidated ? 'edit' : 'create'}
      header={
        <LayoutHeader
          template={initialData?.form?.templateName}
          supplier={initialData?.form?.supplier_id}
          phone={initialData?.form?.phone_number}
          email={initialData?.form?.email}
        />
      }
    />
  );
};

export default UpdateLaboratoryScannedDoc;
